import { useEffect, useState } from 'react';
import { Button } from '@tremor/react';
import clsx from 'clsx';
import { Mail, RefreshCw } from 'lucide-react';
import { ResendingStatus } from './VerifyEmail';
import { ResendStatus } from './ResendStatus';
import { AuthContactFooter } from '../FirebaseAuth/components/AuthContactFooter';
import {
  authTremorButtonNoFocusRing,
  authTremorPrimarySubmitClass,
} from '../FirebaseAuth/authFormTremor';

interface Props {
  readonly handleResend: () => Promise<void>;
  readonly resendingStatus: ResendingStatus;
  readonly handleRefreshVerification?: () => Promise<void>;
  readonly isRefreshingVerification?: boolean;
}

export function VerifyEmailCard({
  handleResend,
  resendingStatus,
  handleRefreshVerification,
  isRefreshingVerification = false,
}: Props) {
  const [countdown, setCountdown] = useState(0);
  const [isCountingDown, setIsCountingDown] = useState(false);

  useEffect(() => {
    if (!isCountingDown || countdown <= 0) return;

    const timer = setTimeout(() => {
      setCountdown((prev) => {
        const newValue = prev - 1;
        if (newValue <= 0) {
          setIsCountingDown(false);
        }
        return newValue;
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [isCountingDown, countdown]);

  const handleResendClick = async () => {
    await handleResend();
    setCountdown(60);
    setIsCountingDown(true);
  };

  const isSending = resendingStatus === ResendingStatus.Sending;

  return (
    <div className="flex flex-1 items-center justify-center px-3 sm:px-5">
      <div className="w-full max-w-2xl">
        <div className="space-y-4 rounded-2xl bg-white/95 p-5 shadow-xl sm:p-6 md:p-7">
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 scale-90 rounded-full bg-primary-400/40 opacity-60 blur-lg animate-pulse" />
              <div className="relative rounded-full bg-primary-500 p-3.5">
                <Mail className="h-9 w-9 text-white" />
              </div>
            </div>
          </div>

          <div className="space-y-1 text-center">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              Verify your email
            </h1>
            <p className="text-sm leading-snug text-gray-600 sm:text-base">
              We&apos;ve sent a verification message to your email address
            </p>
          </div>

          <div className="space-y-1 rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm leading-snug text-gray-800">
            <p>Click the verification link in the email to activate your account.</p>
            <p>If you don&apos;t see the message, check your spam folder.</p>
          </div>

          <div className="space-y-3">
            {handleRefreshVerification && (
              <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/80 p-4">
                <p className="text-sm leading-relaxed text-gray-600">
                  If you opened the verification link in another browser, click below to
                  refresh your account status.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  color="rose"
                  icon={RefreshCw}
                  onClick={handleRefreshVerification}
                  disabled={isRefreshingVerification}
                  loading={isRefreshingVerification}
                  loadingText="Checking…"
                  className={clsx(
                    'w-full !gap-2 !border-gray-300 !bg-white !font-medium !text-gray-800 !shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:!bg-gray-50 hover:!shadow-md disabled:!translate-y-0 disabled:!opacity-60',
                    '!py-2.5',
                    authTremorButtonNoFocusRing,
                  )}
                >
                  Check verification status
                </Button>
              </div>
            )}

            <Button
              type="button"
              size="sm"
              color="rose"
              icon={isCountingDown ? undefined : RefreshCw}
              onClick={handleResendClick}
              disabled={isSending || isCountingDown}
              loading={isSending}
              loadingText="Sending…"
              className={clsx(authTremorPrimarySubmitClass, '!py-2.5')}
            >
              {isCountingDown ? `${countdown} Resend` : 'Resend'}
            </Button>

            <ResendStatus resendingStatus={resendingStatus} />
          </div>

          <AuthContactFooter />
        </div>
      </div>
    </div>
  );
}
